import React, { useEffect, useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import { Link, Navigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { canAccessEngineeringModule } from '../../utils/roleConstants';
import { getStoredUser } from '../Tooling/tpmRequest';
import { authenticatedIdentity } from '../../_services/apiFetch';
import { machiningMaintenanceService as service } from '../../_services/machiningMaintenance.service';
import { decideApproval } from '../../_services/approval.service';
import { AssetMaintenanceRecord } from './AssetMaintenanceRecord';
import { AssetMaintenanceKpis } from './AssetMaintenanceKpis';

export function downtimeLabel(request, now = Date.now()) {
    if (!request.downtimeStartedAt) return 'Unavailable';
    const end = request.returnedToServiceAt ? Date.parse(request.returnedToServiceAt) : now;
    const duration = end - Date.parse(request.downtimeStartedAt);
    if (!Number.isFinite(duration) || duration < 0) return 'Unavailable';
    return `${(duration / 3600000).toFixed(2)} h${request.returnedToServiceAt ? '' : ' (live)'}`;
}

function reportedLabel(value) {
    if (!value) return '—';

    const date = moment.utc(value).utcOffset(8);
    return date.isValid() ? date.format('YYYY-MM-DD HH:mm') : '—';
}

function SummaryContent({ kind, slug, title, requestId, create, identity }) {
    const base = `/tpm/${slug}/summary`;
    const [query, setQuery] = useState({
        page: 1,
        pageSize: 25,
        status: '',
        search: '',
        from: '',
        to: '',
        eventType: '',
    });
    const [search, setSearch] = useState('');
    const invalidFilter = React.useRef(null);
    const [missingFilter, setMissingFilter] = useState(false);
    const [missingDetail, setMissingDetail] = useState(false);
    const [list, setList] = useState(null);
    const [metrics, setMetrics] = useState(null);
    const [metricsPending, setMetricsPending] = useState(false);
    const [metricsError, setMetricsError] = useState(null);
    const pendingList = React.useRef(false);
    const [detail, setDetail] = useState(null);
    const [error, setError] = useState(null);
    const [denied, setDenied] = useState(false);
    const [refresh, setRefresh] = useState(0);
    const queryKey = JSON.stringify(query);
    const metricsKey = JSON.stringify(
        Object.fromEntries(
            Object.entries(query).filter(([key]) => !['page', 'pageSize'].includes(key))
        )
    );

    useEffect(() => {
        const timer = setTimeout(() => {
            setQuery((previous) => previous.search === search
                ? previous
                : { ...previous, search, page: 1 }
            );
        }, 300);

        return () => clearTimeout(timer);
    }, [search]);

    function failed(failure) {
        if (failure.status === 404 && !requestId && !create) {
            invalidFilter.current = metricsKey;
            setMissingFilter(true);
            setError(null);
            setList(null);
            setMetrics(null);
            setMetricsError(null);
            setMetricsPending(false);
            pendingList.current = false;
            service.clear();
            return;
        }

        setError(failure);
        if (failure.status === 404) {
            setMissingDetail(true);
            setDetail(null);
        }

        if ([401, 403].includes(failure.status)) {
            setDenied(true);
            setDetail(null);
            setList(null);
            setMetrics(null);
            service.clear();
        }
    }

    async function mutation(operation, notify = true) {
        try {
            const result = await operation();
            if (identity !== authenticatedIdentity()) throw new Error('Account changed.');
            if (notify) window.dispatchEvent(new Event('refreshPendingApprovalCounts'));
            return result;
        } catch (failure) {
            if ([401, 403, 404].includes(failure.status)) failed(failure);
            throw failure;
        }
    }

    useEffect(() => {
        if (create) return;
        invalidFilter.current = null;
        setMissingFilter(false);
        setMissingDetail(false);
        setError(null);
        let active = true;
        let pending = false;
        let offline = false;
        const current = () =>
            active && identity === authenticatedIdentity() && invalidFilter.current !== metricsKey;
        async function load(force = false) {
            if (!current() || pending || offline) return;
            pending = true;
            try {
                if (requestId) {
                    const result = await service.getRequest(kind, requestId);
                    if (current()) setDetail(result);
                } else {
                    const read = service.listSummary(kind, JSON.parse(queryKey), force);
                    if (read.data && current()) setList(read.data);
                    const result = await read.revalidate;
                    if (current()) {
                        setList(result);
                        pendingList.current = result.items.some((item) =>
                            item.processStatus === 'PendingApproval' ||
                            (item.eventType === 'breakdown' && item.downtimeStartedAt && !item.returnedToServiceAt)
                        );
                    }
                }
                if (current()) setError(null);
            } catch (failure) {
                offline = !failure.status;
                if (current()) failed(failure);
            } finally {
                pending = false;
            }
        }
        const probe = () => {
            if (document.hidden || navigator.onLine === false) return;
            offline = false;
            load(true);
        };
        if (!requestId) {
            setList(null);
            pendingList.current = false;
        }
        load(refresh > 0);
        const timer = !requestId
            ? setInterval(() => {
                  if (pendingList.current && !document.hidden && navigator.onLine !== false) load(true);
              }, 60000)
            : null;
        window.addEventListener('focus', probe);
        window.addEventListener('online', probe);
        return () => {
            active = false;
            clearInterval(timer);
            window.removeEventListener('focus', probe);
            window.removeEventListener('online', probe);
        };
    }, [kind, requestId, create, identity, queryKey, refresh]);

    useEffect(() => {
        if (create || requestId) return;
        let active = true;
        let pending = false;
        let offline = false;
        const current = () =>
            active && identity === authenticatedIdentity() && invalidFilter.current !== metricsKey;
        async function load(force = false) {
            if (!current() || pending || offline) return;
            pending = true;
            setMetricsPending(true);
            try {
                const read = service.listMetrics(kind, JSON.parse(metricsKey), force);
                if (read.data && current()) setMetrics(read.data);
                const result = await read.revalidate;
                if (current()) {
                    setMetrics(result);
                    setMetricsError(null);
                }
            } catch (failure) {
                offline = !failure.status;
                if (current()) {
                    setMetricsError(failure);
                    if ([401, 403, 404].includes(failure.status)) failed(failure);
                }
            } finally {
                pending = false;
                if (current()) setMetricsPending(false);
            }
        }
        setMetrics(null);
        setMetricsError(null);
        load(refresh > 0);
        const probe = () => {
            if (document.hidden || navigator.onLine === false) return;
            offline = false;
            load(true);
        };
        const timer = setInterval(() => {
            if (pendingList.current && !document.hidden && navigator.onLine !== false) load(true);
        }, 60000);
        window.addEventListener('focus', probe);
        window.addEventListener('online', probe);
        return () => {
            active = false;
            clearInterval(timer);
            window.removeEventListener('focus', probe);
            window.removeEventListener('online', probe);
        };
    }, [kind, requestId, create, identity, metricsKey, refresh]);

    return (
        <div className="container py-3">
                {!requestId && !create && <>
                    <div style={{ marginBottom: 20 }}>
                        <h4 style={{ marginBottom: 4 }}>{title} Asset Maintenance &amp; Downtime Summary</h4>
                        <div style={{ color: '#6c757d' }}>Aggregated production-ready tracker for structural failure and maintenance KPIs.</div>
                    </div>
                </>}
                {error && <Alert variant="danger">{error.message}</Alert>}
                {denied ? (
                    <p>Maintenance access is unavailable.</p>
                ) : create ? (
                    <Navigate to={base} replace />
                ) : requestId ? (
                    detail ? (
                        <AssetMaintenanceRecord
                            key={detail.id}
                            detail={detail}
                            kind={kind}
                            title={title}
                            base={base}
                            onDecide={(action, reason) =>
                                mutation(() =>
                                    decideApproval(
                                        'machining_maintenance',
                                        requestId,
                                        action,
                                        reason
                                    ),
                                    false
                                )
                            }
                            onReload={() => mutation(() => service.getRequest(kind, requestId))}
                        />
                    ) : (
                        <p>{missingDetail ? 'Request is no longer available.' : 'Loading request…'}</p>
                    )
                ) : (
                    <>
                        <AssetMaintenanceKpis metrics={metrics} pending={metricsPending} error={metricsError} />
                        {missingFilter && <Alert variant="warning">Maintenance data is no longer available. Refresh to retry.</Alert>}
                        <Button variant="outline-secondary" className="mb-3" onClick={() => setRefresh((value) => value + 1)}>
                            Refresh
                        </Button>
                        <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
                            <Form.Control
                                aria-label="Search maintenance"
                                type="search"
                                placeholder="Search ticket ID, asset number or name"
                                value={search}
                                style={{ flex: '2 1 280px', width: 'auto' }}
                                onChange={(event) => setSearch(event.target.value)}
                            />
                            {['from', 'to'].map((field) => (
                                <Form.Group
                                    key={field}
                                    controlId={`maintenance-${kind}-${field}`}
                                    style={{ flex: '1 1 200px', width: 'auto' }}
                                >
                                    <Form.Label>{field === 'from' ? 'From date' : 'To date'}</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={query[field]}
                                        onChange={(event) =>
                                            setQuery((previous) => ({
                                                ...previous,
                                                [field]: event.target.value,
                                                page: 1,
                                            }))
                                        }
                                    />
                                </Form.Group>
                            ))}
                            <Form.Select
                                aria-label="Event type"
                                value={query.eventType}
                                style={{ flex: '1 1 200px', width: 'auto' }}
                                onChange={(event) => setQuery((previous) => ({ ...previous, eventType: event.target.value, page: 1 }))}
                            >
                                <option value="">All events</option>
                                {['breakdown', 'preventive', 'other'].map((eventType) => <option key={eventType}>{eventType}</option>)}
                            </Form.Select>
                            <Form.Select
                                aria-label="Status"
                                value={query.status}
                                style={{ flex: '1 1 200px', width: 'auto' }}
                                onChange={(event) =>
                                    setQuery((previous) => ({
                                        ...previous,
                                        status: event.target.value,
                                        page: 1,
                                    }))
                                }
                            >
                                <option value="">All statuses</option>
                                {['NotStarted', 'PendingApproval', 'Approved', 'Rejected'].map(
                                    (status) => (
                                        <option key={status}>{status}</option>
                                    )
                                )}
                            </Form.Select>
                        </div>
                        <h5>Historical {title} Repair &amp; Maintenance Ledger</h5>
                        <DataTable
                            columns={[
                                {
                                    name: 'Ticket / Asset',
                                    grow: 2,
                                    cell: (item) => (
                                        <div className="py-2">
                                            <Link to={base + '/requests/' + item.id}>{item.ticketId}</Link>
                                            <br />
                                            {item.asset.name || item.asset.machine || item.asset.testerNo || item.asset.jigFixNo}
                                        </div>
                                    ),
                                },
                                { name: 'Event', selector: (item) => item.eventType },
                                {
                                    name: 'Status',
                                    selector: (item) => item.processStatus + (item.approval?.canApprove ? ' · Your approval' : ''),
                                    grow: 2,
                                },
                                { name: 'Downtime', selector: (item) => downtimeLabel(item) },
                                { name: 'Cost (MYR)', selector: (item) => item.repairCost.toFixed(2) },
                                {
                                    name: 'Reported',
                                    selector: (item) => reportedLabel(item.reportedAt),
                                    sortable: true,
                                    minWidth: '165px',
                                },
                            ]}
                            data={list?.items || []}
                            striped
                            highlightOnHover
                            persistTableHead
                            progressPending={!list && !error && !missingFilter}
                            pagination
                            paginationServer
                            paginationTotalRows={list?.total || 0}
                            paginationPerPage={query.pageSize}
                            paginationDefaultPage={query.page}
                            paginationResetDefaultPage={query.page === 1}
                            paginationRowsPerPageOptions={[10, 25, 50, 100]}
                            onChangePage={(page) => setQuery((previous) => ({ ...previous, page }))}
                            onChangeRowsPerPage={(pageSize) => setQuery((previous) => ({ ...previous, pageSize, page: 1 }))}
                            noDataComponent="No maintenance requests match these filters."
                        />
                    </>
                )}
        </div>
    );
}

export function AssetMaintenanceSummary(props) {
    const { requestId } = useParams();
    const [identity, setIdentity] = useState(authenticatedIdentity);
    useEffect(() => {
        const update = () => setIdentity(authenticatedIdentity());
        const timer = setInterval(update, 1000);
        window.addEventListener('storage', update);
        return () => {
            clearInterval(timer);
            window.removeEventListener('storage', update);
        };
    }, []);
    if (!canAccessEngineeringModule(getStoredUser()))
        return <Alert variant="danger">Engineering access is required.</Alert>;
    return (
        <SummaryContent
            key={identity + props.kind + (requestId || '') + Boolean(props.create)}
            {...props}
            requestId={requestId}
            identity={identity}
        />
    );
}
